/**
 * Identity & nickname helpers (MATCHMAKING.md §4, PROTOCOL.md §3.1).
 * Ids are random, unguessable and server-issued; `playerId` + `resumeToken`
 * double as the reconnect credential, persisted by the client.
 */
import { customAlphabet } from 'nanoid';
import { NICKNAME_MAX_LEN, PARTY_CODE_ALPHABET, PARTY_CODE_LENGTH } from './constants.js';

const hex = customAlphabet('0123456789abcdef');
const partyCode = customAlphabet(PARTY_CODE_ALPHABET, PARTY_CODE_LENGTH);

export function generatePlayerId(): string {
  return `p_${hex(8)}`;
}

export function generateResumeToken(): string {
  return hex(16);
}

export function generatePartyCode(): string {
  return partyCode();
}

export function generateMatchId(): string {
  return `m_${hex(10)}`;
}

const FALLBACK_NICKNAMES = ['Marble', 'Hopper', 'Jumper', 'Glassy'] as const;

// C0/C1 control chars + DEL — never allowed in a display name.
// Written with \x escapes so no raw control bytes live in this source file.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = new RegExp('[\\x00-\\x1F\\x7F-\\x9F]', 'g');

/**
 * Sanitize a client-supplied nickname: trim, strip control characters,
 * collapse whitespace, clamp to NICKNAME_MAX_LEN. Returns null when nothing
 * usable remains (caller answers NICKNAME_INVALID or picks a fallback).
 */
export function sanitizeNickname(raw: string): string | null {
  // Whitespace collapse runs FIRST so \t/\n become spaces instead of being
  // deleted as control chars (better display-name UX).
  const cleaned = raw.replace(/\s+/g, ' ').replace(CONTROL_CHARS, '').trim();
  if (cleaned.length === 0) return null;
  return cleaned.slice(0, NICKNAME_MAX_LEN);
}

/** Deterministic fallback when a nickname is missing/invalid. */
export function fallbackNickname(playerId: string): string {
  const base = FALLBACK_NICKNAMES[playerId.length % FALLBACK_NICKNAMES.length] ?? 'Player';
  return `${base}-${playerId.slice(-4)}`;
}
